/**
 * packages/ui/failure.mjs — ONE VOCABULARY FOR "THIS DID NOT WORK", ACROSS EVERY CITECLERK SURFACE.
 *
 * Framework-free and dependency-free on purpose: `apps/main` (Clark, Citations, Doc Check),
 * `apps/steel` (Research) and `apps/seafoam` (Draft) all import it, and two of those are React while
 * the marketing pages are not.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 WHY IT EXISTS — TWO DEFECTS, ONE CAUSE.
 *
 * 1. A SIGNED-OUT USER WAS NEVER TOLD THEY WERE SIGNED OUT. Research's answer stream is an
 *    EventSource, which cannot send an Authorization header and so authenticates by cookie alone.
 *    With no Clerk session steel returns 401 and the UI rendered NOTHING — no error, no retry, no
 *    empty state. To the user an expired session was an infinite hang. The same 401 reaching Clark
 *    produced a generic "something went wrong". In neither case did the product say the one thing
 *    that would have let the user fix it in five seconds.
 *
 *    ⇒ `needsSignIn` is a first-class outcome here, not a subtype of "error", and every surface that
 *      renders a failure must branch on it.
 *
 * 2. A RETRIEVAL FAILURE READ AS AN ANSWER. When Clark's research turn failed, the turn fell through
 *    to the persona, which answered the legal question from model memory — naming Harlow, Saucier,
 *    Pearson, White v. Pauly and Kisela with no citations, no pincites, no gate, and asserting a
 *    treatment relationship ("Pearson overruled Saucier's mandatory sequencing") with no citator
 *    behind it — rendered identically to a verified answer.
 *
 *    ⇒ Every message below states WHAT FAILED and, where authority was expected, that NOTHING WAS
 *      RETRIEVED. A failure must never read as a quieter answer.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THE COPY CONSTRAINTS ARE PART OF THE CONTRACT, NOT A STYLE PREFERENCE.
 *   · One line wherever the meaning survives it.
 *   · No exclamation marks. No "Oops", no "Uh oh", no apology theatre.
 *   · Never restate the user's question back at them — they can see it directly above.
 *   · Name the retry as an action the user takes, never as a promise we will retry for them.
 * The visual constraints that go with these (no box, no icon, no amber, muted foreground) live with
 * each surface's own styles; this module owns the WORDS and the CLASSIFICATION.
 */

/**
 * @typedef {object} Failure
 * @property {string}  kind        stable machine id — safe to branch on, safe to log
 * @property {string}  message     the single user-facing line
 * @property {boolean} canRetry    show an inline retry affordance
 * @property {boolean} needsSignIn the cause is an absent/expired session; offer sign-in
 * @property {boolean} isConfig    a deployment fault, not a user fault — surface loudly, log loudly
 */

/** @type {Record<string, Failure>} */
export const FAILURES = {
  SIGNED_OUT: {
    kind: 'signed-out',
    message: 'You’re signed out, so nothing was retrieved. Sign in and send it again.',
    canRetry: false, needsSignIn: true, isConfig: false,
  },
  SESSION_EXPIRED: {
    kind: 'session-expired',
    message: 'Your session expired, so nothing was retrieved. Sign in and send it again.',
    canRetry: false, needsSignIn: true, isConfig: false,
  },
  // 🔴 The wording is deliberate: "no authority was retrieved" is the fact the user needs, because
  // the alternative reading — that we answered from somewhere else — is the defect this replaces.
  RETRIEVAL_UNREACHABLE: {
    kind: 'retrieval-unreachable',
    message: 'I couldn’t reach the research engine, so no authority was retrieved.',
    canRetry: true, needsSignIn: false, isConfig: false,
  },
  RETRIEVAL_FAILED: {
    kind: 'retrieval-failed',
    message: 'The research engine couldn’t complete that one, so no authority was retrieved.',
    canRetry: true, needsSignIn: false, isConfig: false,
  },
  RETRIEVAL_TIMEOUT: {
    kind: 'retrieval-timeout',
    message: 'That took longer than expected, so I stopped waiting. No authority was retrieved.',
    canRetry: true, needsSignIn: false, isConfig: false,
  },
  RETRIEVAL_TRUNCATED: {
    kind: 'retrieval-truncated',
    message: 'That answer was cut off before it finished.',
    canRetry: true, needsSignIn: false, isConfig: false,
  },
  // 🔴 A CONFIG FAULT, AND IT SAYS SO. Previously `CLARK_RETRIEVAL` being unset made Clark silently
  // fall back to the citation resolver, which then tried to FORMAT "42 U.S.C. 1983" as a statute and
  // replied "Let me try that again with the standard federal code form." A user asking a research
  // question received a citation-formatting error and no indication that research was switched off.
  RETRIEVAL_DISABLED: {
    kind: 'retrieval-disabled',
    message: 'Research is switched off in this deployment, so no authority was retrieved.',
    canRetry: false, needsSignIn: false, isConfig: true,
  },
  QUOTA: {
    kind: 'quota',
    message: 'You’re out of research questions for this month.',
    canRetry: false, needsSignIn: false, isConfig: false,
  },
  /* 🔴🔴 RUN GC1 — A POLICY REFUSAL IS NOT AN ENGINE FAILURE, AND UNTIL THIS ENTRY EXISTED THERE
     WAS NO WAY FOR ONE TO SAY SO. Research's quota block arrives as an SSE `error` frame carrying
     `{error:'limit_reached', …}` and NEITHER `status` NOR `reason`; it therefore fell through every
     branch below to `status == null` → RETRIEVAL_UNREACHABLE, and the product told a
     paying-candidate customer "I couldn't reach the research engine". The engine was fine.

     `isPolicy` is the field surfaces branch on: it means the refusal is about the plan, so the
     right rendering is the money modal and NOT an error line with a retry. `canRetry` is false
     because retrying a plan refusal cannot succeed, and `message` is deliberately plain — a surface
     that has not been taught about the modal still renders a true sentence rather than blaming us.
     The MEANINGFUL copy comes from the server's own body via meterBlock.mjs; this is the floor. */
  PLAN_BLOCKED: {
    kind: 'plan-blocked',
    message: 'That isn’t included on your current plan, so nothing was retrieved.',
    canRetry: false, needsSignIn: false, isConfig: false, isPolicy: true,
  },
  /* ══════════════════════════════════════════════════════════════════════════════════════════
     🔴🔴 RUN HB — FOUR MORE REFUSALS THAT READ AS AN OUTAGE, AND THEY ARE NOT ONE MESSAGE.

     RUN GC1 built one recogniser and one modal and taught them five codes. The mechanism it built
     is A LIST, and a list is only as complete as the last person to add to it. RUN GY1 then
     measured four codes steel emits that were not on it — every one of them falling to
     RETRIEVAL_FAILED, so the product told the customer "The research engine couldn't complete that
     one" about an engine that was working perfectly. Measured before the change, all four:
     ops/run-hb/out/step0-wire-BEFORE.json.

     ⭐ THE POINT OF THIS BLOCK IS THAT THEY ARE FOUR DIFFERENT SENTENCES. Collapsing them into one
     "policy" line would fix the classification and keep the lie: telling someone whose CARD WAS
     DECLINED that the thing "isn't included on your plan" is a different false sentence, not fewer.
     So each carries the copy its own meaning requires, and each says what the user can actually do.

     🔴 WHY `isPolicy` IS FALSE ON ALL FOUR, AND `isAccount` IS THE NEW FIELD.
     `isPolicy` is not a synonym for "not our fault" — it is a RENDERING INSTRUCTION. Surfaces read
     it as "suppress the failure row, the money modal is showing this instead"
     (steel Workspace.jsx:1027 renders StreamFailure only when `!isPolicy`). It is therefore only
     safe to set when a modal will genuinely open, i.e. when `readMeterBlock` returns a body.
     It returns null for all four — deliberately, and for four separately measured reasons recorded
     in meterBlock.mjs. Setting `isPolicy` here would suppress the row with nothing behind it and
     re-create, in one field, THE EMPTY BUBBLE THIS WHOLE ARC EXISTS TO REMOVE.

     `isAccount` therefore means: the refusal is about the user's account — their allowance, their
     matter count, or their card — and NOT about our service. It leaves the honest-failure row in
     place (which is where the user is already looking) and fills it with a true sentence and no
     retry button, because retrying cannot succeed. */
  /* ⚠️ ALL FOUR OF THESE WERE TOO LONG ON THE FIRST DRAFT (99, 97, 113, 111 chars) and
     apps/main/test/clarkFailureState.test.js caught every one: "one line wherever the meaning
     survives it" is a CONTRACT here, enforced at 90 characters, not a style preference. Each was
     cut to the three things it has to carry — what ran out or went wrong, that nothing happened,
     and the one action that works — and nothing else. */
  OUT_OF_QUERIES: {
    kind: 'out-of-queries',
    /* ⚠️ NO PURCHASE IS OFFERED HERE. `ALACARTE_LIVE` is set nowhere (alacarte.js:93 reads it as
       `=== '1'`), so every pack route answers 403 `alacarte_disabled` — GB1 measured it and GC1's
       modal comment records it. The server's own sentence on this refusal says "Buy a 10-pack to
       keep going", which is a button that cannot work; this says WHAT RAN OUT and WHAT RESTORES IT
       and leaves the buy path to the flag decision. It also drops the retired noun "searches",
       which meterBlockContract already bans from meterNoun. */
    message: 'You’re out of research queries, so nothing was retrieved. A query pack adds more.',
    canRetry: false, needsSignIn: false, isConfig: false, isAccount: true,
  },
  MATTER_CAP: {
    kind: 'matter-cap',
    /* 🔴 STRUCTURAL, NOT SELLABLE — AND THE DIFFERENCE IS THE WHOLE POINT. The 402 on
       /api/projects is emitted by TWO gates: this flat per-account ceiling (`matter_cap_reached`,
       `upgrade:false`, delete-to-make-room) and the monthly matters pool (`limit_reached`, which
       RUN MB also set to `upgrade:false` when the pool went flat at 999 on every tier). In a drain that is the difference between selling a tier and telling someone
       to archive, and entitlement.js:406 already carries its own warning that offering an upgrade
       which cannot lift the limit is worse than the cap itself. They arrive under different codes,
       so they are distinguished here by code and not by guess; the pool code keeps the meter-block
       path it already had. No number is repeated — the cap is the server's to state. */
    message: 'This account holds all the matters it can. Delete one — no plan raises this limit.', // RUN MB: "archive" is our word, not the lawyer's; the control says Delete
    canRetry: false, needsSignIn: false, isConfig: false, isAccount: true,
  },
  PAYMENT_ACTION_REQUIRED: {
    kind: 'payment-action-required',
    /* ⚠️ AND IT DOES NOT PROMISE AN EMAIL. The shipped modal for this code says "we'll email you a
       link to finish"; RUN GB1 measured the email path (steel/routes/billing.js:29 `emailPath`) to
       be a `console.warn` stub, so that sentence is currently false. Naming Settings is the one
       action that is actually available today. */
    message: 'Your bank needs to verify that payment. Re-authenticate your card in Settings.',
    canRetry: false, needsSignIn: false, isConfig: false, isAccount: true,
  },
  PAYMENT_DECLINED: {
    kind: 'payment-declined',
    // A card problem — not a plan problem, and emphatically not an outage. Nothing was charged.
    message: 'Your card was declined, so nothing was charged. Update your card in Settings.',
    canRetry: false, needsSignIn: false, isConfig: false, isAccount: true,
  },
  NETWORK: {
    kind: 'network',
    message: 'The connection dropped, so no authority was retrieved.',
    canRetry: true, needsSignIn: false, isConfig: false,
  },
};

/** HTTP statuses that mean "we do not know who you are", as opposed to "you may not do that". */
const AUTH_STATUSES = new Set([401]);

/**
 * Classify a failed request into exactly one `Failure`.
 *
 * @param {object} input
 * @param {number} [input.status]  HTTP status, when there was a response
 * @param {string} [input.reason]  a machine reason the server sent (`retrieval_disabled`, …)
 * @param {string} [input.abort]   our own abort reason (`idle` | `total`)
 * @param {boolean}[input.enabled] the server's `enabled` field, when present
 * @param {boolean}[input.capped]  the server's quota flag
 * @param {boolean}[input.truncated] the stream ended without a terminal frame
 * @param {boolean}[input.network] the request never reached a server
 * @param {string} [input.policy]  the body's own machine code (`error`), when there was a body
 * @param {boolean}[input.featureLocked] main's `featureGate` camelCase flag — the one refusal shape
 *                                 that carries a SENTENCE in `error` and no machine code at all
 * @returns {Failure}
 *
 * 🔴 ORDER IS LOAD-BEARING. Sign-in is tested FIRST: a 401 that also carries `enabled:false` is
 * still, to the user, "sign in" — telling them the feature is off would send them to support for a
 * problem they can fix themselves. Quota precedes the generic failures for the same reason.
 */
export function classifyFailure({ status, reason, abort, enabled, capped, truncated, network, policy, featureLocked } = {}) {
  if (AUTH_STATUSES.has(Number(status)) || reason === 'signed_out' || reason === 'unauthenticated') {
    return reason === 'session_expired' ? FAILURES.SESSION_EXPIRED : FAILURES.SIGNED_OUT;
  }
  if (reason === 'session_expired') return FAILURES.SESSION_EXPIRED;
  /* 🔴 RUN GC1 — POLICY IS TESTED BEFORE EVERY ENGINE BRANCH AND AFTER SIGN-IN.
     After sign-in, because a signed-out user cannot fix a plan problem they do not have. Before the
     engine branches, because a 403/402/429 that carries a policy code is NOT a failure of ours and
     must never be able to reach a sentence that says it is. `policy` is passed by the caller as the
     body's own machine code (`error`), which is the field every gate in this repo actually sets —
     see meterBlock.mjs for the measured list of shapes and why reading `reason` alone missed them. */
  if (isPolicyCode(policy) || isPolicyCode(reason)) return FAILURES.PLAN_BLOCKED;
  /* 🔴 RUN HB — THE ACCOUNT CODES SIT IN EXACTLY THE SAME SLOT, FOR EXACTLY THE SAME REASON.
     After sign-in (a signed-out user cannot fix a card they have not been asked for) and before
     every engine branch (a 402 that names the account must never be able to reach a sentence that
     blames our engine). Same lookup shape as policy, and the same two fields, because the emitters
     put the code in `error` on some routes and in `reason` on others. */
  const acct = accountFailureFor(policy) || accountFailureFor(reason);
  if (acct) return acct;
  /* 🔴🔴 RUN HB — GX1's camelCase SHAPE WAS KNOWN TO ONE HALF OF THE RECOGNISER AND NOT THE OTHER,
     AND MY OWN STEP-5 ARM IS WHAT CAUGHT IT.
     main's `featureGate` (apps/main/server/lib/plan.js:198) answers 403 with
     `{ error: "<a full sentence>", featureLocked: true, feature, plan }` — the SENTENCE in `error`
     and NO machine code. `readMeterBlock` learned that shape in RUN GC1 and opens the modal for it.
     `classifyFailure` never did: with a sentence in `policy` no code matches, so it fell through to
     RETRIEVAL_FAILED — "The research engine couldn't complete that one", **with a retry button**.
     On Clark both are called on the same body, so the money modal opened on top of a failure row
     blaming our engine: GC1's "contradiction with an extra step", surviving in the one shape its
     own contract test did not cover.
     ⚠️ THE CALLER MUST PASS IT. `featureLocked` is not derivable from a sentence, so a call site
     that reads only `body.error` cannot be fixed from inside this function — see the hand-back. */
  if (featureLocked === true) return FAILURES.PLAN_BLOCKED;
  if (capped || Number(status) === 429) return FAILURES.QUOTA;
  if (enabled === false || reason === 'retrieval_disabled') return FAILURES.RETRIEVAL_DISABLED;
  if (abort === 'idle' || abort === 'total' || reason === 'timeout') return FAILURES.RETRIEVAL_TIMEOUT;
  /* 🔴 RUN AP — `parse` IS A REACHED-IT-AND-COULD-NOT-READ-IT FAILURE, NOT AN UNREACHABLE ONE.
     `toV2Failure` now stamps `error.kind: 'parse'` on the three reasons that mean the engine
     answered and the envelope could not be read back (steel/lib/clarkAnswerV2.js). Without this
     line the reason falls all the way through to the `status == null` case and classifies as
     RETRIEVAL_UNREACHABLE — so the row would carry a "couldn't reach the engine" CLASSIFICATION
     underneath a message that correctly says we reached it. RUN AN caught the mismatch from the
     copy side; this is the same mismatch on the classification side, and both halves have to agree
     or the next surface to render from `failure.kind` reintroduces the wrong words. */
  if (reason === 'parse') return FAILURES.RETRIEVAL_FAILED;
  if (truncated) return FAILURES.RETRIEVAL_TRUNCATED;
  if (network) return FAILURES.NETWORK;
  if (status == null) return FAILURES.RETRIEVAL_UNREACHABLE;
  return FAILURES.RETRIEVAL_FAILED;
}

/**
 * The machine codes that mean "your plan does not allow this". Kept in step with meterBlock.mjs's
 * recognisers by `packages/ui/meterBlockContract.test.mjs`, which fails if the two lists drift —
 * two places that must agree and no test between them is how a refusal starts reading as an outage
 * again on one surface only.
 */
export const POLICY_CODES = new Set([
  'limit_reached', 'feature_locked',
  'clark_daily_limit_reached', 'clark_anon_limit_reached', 'daily_limit_reached',
]);
function isPolicyCode(c) { return typeof c === 'string' && POLICY_CODES.has(c); }

/**
 * 🔴 RUN HB — THE ACCOUNT CODES, AND WHY THEY ARE A SEPARATE TABLE FROM `POLICY_CODES`.
 *
 * `POLICY_CODES` is not merely "codes we call policy" — meterBlockContract.test.mjs asserts that
 * EVERY member of it is a body `readMeterBlock` recognises, because a code that classifies as
 * policy while producing no modal payload is a suppressed failure row with nothing behind it. That
 * assertion is load-bearing and these four codes would each break it, correctly: none of them is a
 * meter block. `matter_cap_reached` has no meter in PLAN_LIMITS at all, and a declined card is not
 * a quota.
 *
 * So they get their own table, mapping code → the one sentence that code means. The contract test
 * asserts the two tables are DISJOINT and that no account code reads as a meter block — the same
 * kind of guard that keeps failure.mjs and meterBlock.mjs from drifting apart.
 *
 * ⚠️ TWO EMITTERS, ONE CODE, TWO SENTENCES. `authentication_required` and `payment_failed` are each
 * sent from two places with different server copy — searchSpend.js:29 says "the auto-recharge could
 * not be completed" (a charge the user never asked for) and billing.js:35/42 says "the charge could
 * not be completed" (a purchase they clicked). Both are measured in ops/run-hb/out/step0-wire.
 * The sentence here is true of both, and deliberately describes the CARD rather than the occasion.
 */
export const ACCOUNT_CODES = Object.freeze({
  out_of_searches: FAILURES.OUT_OF_QUERIES,
  matter_cap_reached: FAILURES.MATTER_CAP,
  authentication_required: FAILURES.PAYMENT_ACTION_REQUIRED,
  payment_failed: FAILURES.PAYMENT_DECLINED,
});
function accountFailureFor(c) {
  return (typeof c === 'string' && Object.prototype.hasOwnProperty.call(ACCOUNT_CODES, c))
    ? ACCOUNT_CODES[c] : null;
}

/**
 * True when the refusal is about the user's ACCOUNT — allowance, matter count or card — rather than
 * about our service. The third answer between "your plan" and "we broke": a surface that renders a
 * failure row must not call this one "a failure on our side", and must not offer a retry.
 */
export function isAccountFailure(f) { return Boolean(f && f.isAccount); }

/** True when this failure is the plan rather than the product. Convenience for callers. */
export function isPolicyFailure(f) { return Boolean(f && f.isPolicy); }

/** True when this failure is the user's session rather than our service. Convenience for callers. */
export function isSignInFailure(f) { return Boolean(f && f.needsSignIn); }

export default { FAILURES, classifyFailure, isSignInFailure, isPolicyFailure, isAccountFailure,
  POLICY_CODES, ACCOUNT_CODES };
